import {
  Flex,
  Box,
  Text,
  IconButton,
  HStack,
  useToast,
  Input,
  Tooltip,
  Icon,
  Select,
  SimpleGrid,
  InputLeftElement,
  InputGroup,
  Heading,
  Spinner,
} from "@chakra-ui/react";
import { FaTh, FaListUl, FaFileExcel, FaFileSignature } from "react-icons/fa";
import { FiInfo } from "react-icons/fi";
import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import CustomDatePicker from "@/components/CustomDatePicker";
import { userPermissions } from "@/contexts/userPermissions";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import { PermissionEnum } from "@/utils/permissionEnum";
import { getCorbansByCreditPromoterIdPaginated } from "@/services/corban.service";
import { CiSearch } from "react-icons/ci";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import CreditAuctionProposalsPagination from "@/components/CreditAuctionProposalsPage/credit-auction-proposals-pagination";
import { format } from "date-fns";
import { mapGetProposalParams } from "@/services/consignado/mapper/proposalMapper";
import { downloadConsigProposalExcel, getConsignadoProposals } from "@/services/consignado/proposal.service";
import ProposalCardConsignado from "./ProposalCard";
import ProposalNewTableConsignado from "./ProposalNewTable";
import { validateIntervalDays } from "@/utils/consignado/validateIntervalDays.utils";
import { ProposalStatus } from "@/utils/consignado/proposalStatusRenderer";
import { proposalConsignadoStatusLabels } from "@/utils/consignado/getProposalStatusLabel";

export default function ConsignadoProposalsPage() {
  const toast = useToast();
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const [activeTab, setActiveTab] = useState<
    "emAndamento" | "pago" | "cancelado"
  >("emAndamento");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const backgroundStyle = useConditionalBackground();

  const { user } = useEnsureAuthenticated();
  const permissions = userPermissions(user);
  const isPromoter = permissions.some(
    (permissions) => permissions == PermissionEnum.GRANATECH
  );
  const [selectedCorban, setSelectedCorban] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [loadExcel, setLoadExcel] = useState<boolean>(false);

  const { data: corbans } = useQuery({
    queryKey: ['corbans'],
    queryFn: () => getCorbansByCreditPromoterIdPaginated(1, 10, user?.creditPromoterId!),
    select: (response) => response.data
  })

  type ProposalStatusType =
    (typeof ProposalStatus)[keyof typeof ProposalStatus];

  const payedStatuses: ProposalStatusType[] = [ProposalStatus.PAYED];
  const canceledStatuses: ProposalStatusType[] = [
    ProposalStatus.CANCELED,
    ProposalStatus.CANCELED_BY_CLIENT,
    ProposalStatus.REPROVED,
  ];
  const activeStatuses: ProposalStatusType[] = Object.values(
    ProposalStatus
  ).filter(
    (status) =>
      !payedStatuses.includes(status as ProposalStatusType) &&
      !canceledStatuses.includes(status as ProposalStatusType)
  );

  const fetchProposalsData = async () => {
    let statuses;
    switch (activeTab) {
      case "pago":
        statuses = payedStatuses;
        break;
      case "cancelado":
        statuses = canceledStatuses;
        break;
      case "emAndamento":
        statuses = activeStatuses;
        break;
    }

    if (statusFilter) {
      statuses = [statusFilter];
    }

    const statusParam = statuses.length > 0 ? statuses.join(',') : undefined;

    const startDate = dateRange[0] ? new Date(dateRange[0]).toISOString() : null;
    const endDate = dateRange[1] ? new Date(dateRange[1]).toISOString() : null;

    const filters = {
      page,
      pageSize,
      statuses: statusParam,
      sortBy,
      sortDirection,
      searchTerm,
      startDate,
      endDate,
      corbanId: selectedCorban,
    }
    const params = mapGetProposalParams(filters)

    return await getConsignadoProposals(params);
  };

  const staleSeconds = parseInt(import.meta.env.VITE_PROPOSALS_STALE_SECONDS) || 5
  const { data: proposals, isLoading } = useQuery({
    queryKey: [
      "proposals",
      page,
      pageSize,
      activeTab,
      sortBy,
      sortDirection,
      searchTerm,
      dateRange,
      statusFilter,
      selectedCorban,
    ],
    queryFn: fetchProposalsData,
    staleTime: staleSeconds * 1000,
    refetchInterval: staleSeconds * 1000,
    placeholderData: keepPreviousData
  });

  const orderedProposals = useMemo(() => {
    const list = proposals?.data ?? [];
    if (!list.length) return [];

    if (activeTab === "pago") {
      return [...list].sort((a, b) => {
        const aTime = a.data_pagamento
          ? new Date(a.data_pagamento).getTime()
          : 0;
        const bTime = b.data_pagamento
          ? new Date(b.data_pagamento).getTime()
          : 0;

        // Descendente: mais recente primeiro
        return bTime - aTime;
      });
    }

    return list;
  }, [proposals, activeTab]);

  const handleDownload = async () => {
    setLoadExcel(true);
    try {
      let statuses;
      const startDate = dateRange[0] ? new Date(dateRange[0]).toISOString() : null;
      const endDate = dateRange[1] ? new Date(dateRange[1]).toISOString() : null;
      const isIntervalDaysAvailable = validateIntervalDays(startDate!, endDate!)

      if(isIntervalDaysAvailable.hasError){
        toast({
          title: "Intervalo inválido",
          description: isIntervalDaysAvailable?.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return
      }


      switch (activeTab) {
        case "pago":
          statuses = payedStatuses;
          break;
        case "cancelado":
          statuses = canceledStatuses;
          break;
        case "emAndamento":
          statuses = activeStatuses;
          break;
      }

      if (statusFilter) {
        statuses = [statusFilter];
      }   
      
      const statusParam = statuses.length > 0 ? statuses.join(',') : undefined;
      
      const filters = {
        page,
        pageSize,
        statuses: statusParam,
        sortBy,
        sortDirection,
        searchTerm,
        startDate,
        endDate,
        corbanId: selectedCorban,
      }
      
      const params = mapGetProposalParams(filters)
      const blob = await downloadConsigProposalExcel(params);

      let filename = `Propostas_${activeTab}.xlsx`;
      if (dateRange[0] && dateRange[1]) {
        const start = format(dateRange[0], "dd/MM/yyyy");
        const end = format(dateRange[1], "dd/MM/yyyy");
        const dateSegment = start === end ? start : `${start}-${end}`;
        filename = `Propostas_${activeTab}_${dateSegment}.xlsx`;
      }

      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/octet-stream" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Arquivo Excel baixado com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Erro ao gerar o Excel:", error);

      if(error?.status === 404){
        toast({
          title: "Nenhuma proposta encontrada",
          description: "Não existem propostas no período selecionado. Ajuste as datas e tente novamente.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return 
      }

      toast({
        title: "Erro ao gerar o arquivo Excel",
        description: "Tente novamente mais tarde.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadExcel(false);
    }
  };

  const getStatusOptions = () => {
    if (activeTab === "pago") {
      return [
        {
          value: ProposalStatus.PAYED,
          label: proposalConsignadoStatusLabels[ProposalStatus.PAYED],
        },
      ];
    }
    if (activeTab === "cancelado") {
      return [
        {
          value: ProposalStatus.CANCELED,
          label: proposalConsignadoStatusLabels[ProposalStatus.CANCELED],
        },
        {
          value: ProposalStatus.CANCELED_BY_CLIENT,
          label: proposalConsignadoStatusLabels[ProposalStatus.CANCELED_BY_CLIENT],
        },
        {
          value: ProposalStatus.REPROVED,
          label: proposalConsignadoStatusLabels[ProposalStatus.REPROVED],
        },
      ];
    }
    return Object.entries(proposalConsignadoStatusLabels)
      .filter(
        ([status]) =>
          status !== ProposalStatus.PAYED &&
          status !== ProposalStatus.CANCELED &&
          status !== ProposalStatus.CANCELED_BY_CLIENT &&
          status !== ProposalStatus.REPROVED
      )
      .map(([status, label]) => ({ value: status, label }));
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <Box w={"100%"} padding={{ base: "2", md: "4" }}>
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={2}
        color="white"
        boxShadow="md"
        h="auto"
      >
        <Flex
          w='100%'
          flexDir={['column', 'column', 'column', 'row']}
          alignItems={"center"}
        >
          <Box mb={{ base: 0, md: 0 }}>
            <Flex
              justify={{ base: "center", md: "flex-start" }}
              alignItems={"center"}
              gap={4}
            >
              <FaFileSignature size="1.75rem" />
              <Text
                fontSize={{ base: "2xl", md: "4xl" }}
                maxW={{ base: "100%", md: "80%", lg: "70%" }}
                fontWeight={"bold"}
                whiteSpace={['break-spaces', 'nowrap']}
              >
                Proposta Crédito CLT
              </Text>
            </Flex>
          </Box>

          <HStack
            spacing={{ base: 4, md: 6, lg: 8 }}
            justify="space-evenly"
            mt={[5, 5, 5, 0]}
            flexWrap="wrap"
            w='100%'
          >
            <Box
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => {
                setActiveTab("emAndamento");
                setPage(1);
              }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "md", md: "md", lg: "xl" }}
                fontWeight="bold"
                color={activeTab === "emAndamento" ? "white" : "whiteAlpha.800"}
                transition="color 0.3s ease"
                fontFamily="Lexend Deca"
              >
                EM ANDAMENTO
              </Text>
              {activeTab === "emAndamento" && (
                <Box
                  position="absolute"
                  bottom="-0.25rem"
                  left="50%"
                  transform="translateX(-50%)"
                  height="0.25rem"
                  width="2.5rem"
                  bgColor="white"
                  transition="width 0.3s ease, background-color 0.3s ease"
                />
              )}
            </Box>

            <Box
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => {
                setActiveTab("pago");
                setPage(1);
              }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "md", md: "md", lg: "xl" }}
                fontWeight="bold"
                color={activeTab === "pago" ? "white" : "whiteAlpha.800"}
                transition="color 0.3s ease"
                fontFamily="Lexend Deca"
              >
                PAGO
              </Text>
              {activeTab === "pago" && (
                <Box
                  position="absolute"
                  bottom="-0.25rem"
                  left="50%"
                  transform="translateX(-50%)"
                  height="0.25rem"
                  width="2.5rem"
                  bgColor="white"
                  transition="width 0.3s ease, background-color 0.3s ease"
                />
              )}
            </Box>
            <Box
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => {
                setActiveTab("cancelado");
                setPage(1);
              }}
              mb={{ base: 2, md: 0 }}
              px={{ base: 2, md: 4 }}
            >
              <Text
                fontSize={{ base: "md", md: "md", lg: "xl" }}
                fontWeight="bold"
                color={activeTab === "cancelado" ? "white" : "whiteAlpha.800"}
                transition="color 0.3s ease"
                fontFamily="Lexend Deca"
              >
                CANCELADO
              </Text>
              {activeTab === "cancelado" && (
                <Box
                  position="absolute"
                  bottom="-0.25rem"
                  left="50%"
                  transform="translateX(-50%)"
                  height="0.25rem"
                  width={{ base: "1.25rem", sm: "1.875rem", md: "2.5rem" }}
                  bgColor="white"
                  transition="width 0.3s ease, background-color 0.3s ease"
                />
              )}
            </Box>
          </HStack>
        </Flex>
        <Flex
          alignItems="center"
          mt={9}
          flexWrap="wrap"
          justifyContent="space-between"
        >
          <HStack
            spacing={4}
            mb={{ base: 4, md: 0 }}
            flexDirection={{ base: "column", md: "column", lg: "row" }}
            alignItems={{ base: "center", md: "center" }}
            width="100%"
          >
            <Flex w={{ base: "100%", md: "70%", lg: "20%" }}>
              <InputGroup>
                <InputLeftElement mt={{ base: 2, md: 1 }} pointerEvents="none">
                  <CiSearch color="black" size="2rem" />
                </InputLeftElement>
                <Input
                  size={"md"}
                  height={{ base: "3.5rem", md: "3rem" }}
                  placeholder="Pesquisar Propostas..."
                  variant="filled"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  type="text"
                  fontSize={{ base: 10, sm: 10, md: 10, lg: 10, xl: 15 }}
                  bg={"white"}
                  maxLength={30}
                  color="black"
                  _focus={{ bg: "white" }}
                  _hover={{ bg: "white" }}
                  fontFamily="Lexend Deca"
                />
              </InputGroup>
            </Flex>
            <Tooltip
              label={
                <>
                  Filtre por:
                  <br />- Número da Proposta
                  <br />- Nome do Cliente
                  <br />- CPF do Cliente
                  <br />- Vendedor
                  <br />- Id do Corban
                  <br />- Nome do Corban
                </>
              }
              fontSize="lg"
              placement="top"
            >
              <Box mt={{ base: 0, md: 2 }}>
                <Icon as={FiInfo} boxSize={{ base: 0, md: 7 }} color="white" />
              </Box>
            </Tooltip>

            <CustomDatePicker
              dateRange={dateRange}
              onChange={(dates) => setDateRange(dates)}
            />
            <Box
              width={{ base: "100%", md: "70%", lg: "20%" }}
              mt={{ base: 5, md: 0 }}
              flex={1}
            >
              <Tooltip label="FILTRAR STATUS" fontSize="md" placement="bottom">
                <Select
                  placeholder="-------"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  bg="white"
                  height={{ base: "3.5rem", md: "3rem" }}
                  color="black"
                  fontFamily="Lexend Deca"
                  mt={{ base: 5, md: 0 }}
                  width={"100%"}
                  fontSize={{ base: "0.8rem", md: "1rem" }}
                >
                  {getStatusOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Tooltip>
            </Box>
            {user && isPromoter && (
              <Box
                width={{ base: "100%", md: "70%", lg: "20%" }}
                mt={{ base: 5, md: 0 }}
                flex={1}
              >
                <Tooltip label="FILTRAR CORBAN" fontSize="md" placement="bottom">
                  <Select
                    placeholder="-------"
                    value={selectedCorban || ""}
                    onChange={(e) => setSelectedCorban(parseInt(e.target.value))}
                    bg="white"
                    height={{ base: "3.5rem", md: "3rem" }}
                    color="black"
                    fontFamily="Lexend Deca"
                    mt={{ base: 5, md: 0 }}
                    display={user.creditPromoterId ? "block" : "none"}
                    width={"100%"}
                    fontSize={{ base: "0.8rem", md: "1rem" }}
                  >
                    {corbans?.map((corban) => (
                      <option key={corban.id} value={corban.id}>
                        {corban.businessName}
                      </option>
                    ))}
                  </Select>
                </Tooltip>
              </Box>
            )}
            <HStack spacing={2} ml={{ base: "0", md: "auto" }}>
              {loadExcel ? (
                <Spinner
                  size="md"
                  color="primary"
                  thickness="4px"
                  speed="0.65s"
                />
              ) : (
                <IconButton
                  icon={<FaFileExcel />}
                  aria-label="Download Excel"
                  onClick={handleDownload}
                  colorScheme="green"
                />
              )}
              <IconButton
                icon={<FaTh />}
                aria-label="List View"
                onClick={() => setSelectedView("grid")}
                colorScheme="whiteAlpha"
                variant={selectedView === "list" ? "solid" : "outline"}
              />
              <IconButton
                icon={<FaListUl />}
                aria-label="Grid View"
                onClick={() => setSelectedView("list")}
                colorScheme="whiteAlpha"
                variant={selectedView === "grid" ? "solid" : "outline"}
              />
            </HStack>
          </HStack>
        </Flex>
      </Box>
      {isLoading ? (
        <Flex justifyContent="center" mt={100} mb={2} alignItems="center">
          <Spinner size="xl" color="gray.600" />
        </Flex>
      ) : proposals?.data?.length ? (
        <CreditAuctionProposalsPagination
          currentPage={proposals.pagina_atual}
          totalPages={proposals.total_paginas}
          pageSize={pageSize}
          totalItems={proposals.total}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          mt={2}
          mb={2}
        />
      ) : (
        <Heading mt={100} textAlign={"center"} color="gray.600">
          Nenhuma Proposta Encontrada
        </Heading>
      )}

      {selectedView === "grid" ? (
        <SimpleGrid
          templateColumns={{
            base: "1fr",
            sm: "repeat(1,1fr)",
            md: "repeat(2, 1fr)",
            lg: "repeat(2, 1fr)",
            xl: "repeat(3, 1fr)",
            "2xl": "repeat(4,1fr)",
          }}
          spacing={{ base: 2, sm: 3, md: 4 }}
          key={proposals?.pagina_atual ?? 'loading'}
        >
          {orderedProposals.map((proposal, index) => (
            <ProposalCardConsignado key={index} proposal={proposal} activeTab={activeTab} />
          ))}
        </SimpleGrid>
      ) : (
        <ProposalNewTableConsignado
          setSortBy={setSortBy}
          setSortDirection={setSortDirection}
          sortBy={sortBy}
          sortDirection={sortDirection}
          proposals={orderedProposals}
        />
      )}
      {proposals?.data?.length ? (
        <CreditAuctionProposalsPagination
          currentPage={proposals.pagina_atual}
          totalPages={proposals.total_paginas}
          pageSize={pageSize}
          totalItems={proposals.total}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          mt={6}
          mb={2}
        />
      ) : null}
    </Box>
  );
}
